/**
 * ChapterCover.js — Cover Page Preview (OHSE Cover)
 * Visual preview mimicking the PDF cover page.
 *
 * Props:
 *   projectInfo  {object}  derived project info
 *   companyName  {string}  from companyInfo.name
 *   swmsData     {object}  full swms data (for scope_of_works etc.)
 */

export default function ChapterCover({ projectInfo = {}, companyName = '', swmsData = {} }) {
  const cn = companyName || '[Company Name]';

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE Cover</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Cover Page</h2>
      </div>

      {/* Cover Preview Card */}
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden shadow-sm">
        <div className="flex flex-col items-center justify-center py-12 px-8 text-center">
          {/* Title */}
          <p className="text-lg font-semibold text-[#c9a84c] tracking-[0.25em] uppercase mb-4">
            OHSE Policy &amp; SWMS
          </p>

          {/* Decorative divider */}
          <div className="w-20 h-0.5 bg-[#c9a84c] mx-auto mb-6" />

          {/* Project Name */}
          <h1 className="text-2xl font-bold text-[#1a1a2e] tracking-wide uppercase mb-5 leading-tight">
            {projectInfo.project_name || 'Project Name'}
          </h1>

          {/* Second divider */}
          <div className="w-30 h-px bg-[#c9a84c] mx-auto mb-6" />

          {/* Organisation Name */}
          <p className="text-base text-[#6b6560] tracking-wide mb-1">{cn}</p>

          {/* Contact Name (from project) */}
          {projectInfo.client_name && (
            <p className="text-sm text-[#9a938b] mb-1">{projectInfo.client_name}</p>
          )}

          {/* Contact details */}
          <div className="mt-6 text-xs text-[#9a938b] leading-relaxed">
            <p>{projectInfo.project_address || 'Address'}</p>
          </div>
        </div>
      </div>

      {/* Data summary below preview */}
      <div className="bg-[#f8f7f4] border border-[#e0dbd0] rounded-lg p-4">
        <h3 className="text-sm font-semibold text-[#1a1a2e] mb-3">Project & Organisation Details</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <Kv label="Project Name" value={projectInfo.project_name} />
          <Kv label="Organisation" value={cn} />
          <Kv label="Address" value={projectInfo.project_address} />
          <Kv label="Client" value={projectInfo.client_name} />
          <Kv label="State" value={projectInfo.state} />
          <Kv label="Project Type" value={projectInfo.project_type} />
          <Kv label="Start Date" value={projectInfo.project_start_date} />
          <Kv label="End Date" value={projectInfo.project_end_date} />
        </div>
      </div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 min-w-[110px] shrink-0">{label}:</span>
      <span className="text-[#1a1a2e]">{value || '—'}</span>
    </div>
  );
}
